import { createSelector } from 'reselect';
import { array } from 'fp-ts';
import { createAction } from '@mews/framework';
import { ApplicationState, CommanderThunkAction, AsyncAction, WithOrderItemId, OrderItem } from '@mews-commander/types';
import { OrderItemType, ServiceOrderType } from '@mews-commander/data-access';
import { getStringProp } from '@mews/fp';
import { createFetchAction } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import prefix from '../prefix';
import moduleSelector from '../selector';
import {
    createEntitiesReducer,
    createEntityPropertyIncludesSelector,
    createBaseEntitySelectors,
    createEntitySelectorBy,
} from './utils';
import { getServiceOrder } from './serviceOrders';

export const NAME = 'orderItems';

const ORDER_ITEMS_LOADED = prefix('ORDER_ITEMS_LOADED');
const ORDER_ITEMS_UPDATED = prefix('ORDER_ITEMS_UPDATED');
const ORDER_ITEMS_CANCELLED = prefix('ORDER_ITEMS_CANCELLED');
const ORDER_ITEMS_DELETED = prefix('ORDER_ITEMS_DELETED');
const ORDER_ITEMS_REBATED = prefix('ORDER_ITEMS_REBATED');

export const setOrderItems = (orderItems: OrderItem[]) => createAction(ORDER_ITEMS_LOADED, { entities: orderItems });

export const updateOrderItemsBillId =
    ({ ids, billId }: { ids: string[]; billId: string }): CommanderThunkAction =>
    (dispatch, getState) => {
        const items = getOrderItemsByIds(getState(), { ids });
        const updatedItems = items.map((orderItem): OrderItem => ({ ...orderItem, billId }));

        return dispatch(createAction(ORDER_ITEMS_UPDATED, updatedItems));
    };

export const deleteOrderItems = (ids: string[]) => createAction(ORDER_ITEMS_DELETED, ids);

export const cancelOrderItems =
    (ids: string[]): CommanderThunkAction =>
    (dispatch, getState, { container }) => {
        const enterpriseId = getCurrentEnterpriseId();

        const promise = container()
            .orderItemComponent.cancel({ orderItemIds: ids, enterpriseId })
            .then(() => {
                dispatch(deleteOrderItems(ids));
            });

        return createFetchAction(ORDER_ITEMS_CANCELLED, promise);
    };

export const rebateOrderItems =
    ({ ids, reason }: { ids: Array<OrderItem['id']>; reason: string }): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { container }) => {
        const enterpriseId = getCurrentEnterpriseId();

        const promise = container().orderItemComponent.rebate({ orderItemIds: ids, reason, enterpriseId });

        return dispatch(createFetchAction(ORDER_ITEMS_REBATED, promise));
    };

export const orderItemsReducer = createEntitiesReducer({
    FETCH_SUCCESS: ORDER_ITEMS_LOADED,
    UPDATE_SUCCESS: ORDER_ITEMS_UPDATED,
    DELETE_SUCCESS: ORDER_ITEMS_DELETED,
});

export const [getOrderItems, getOrderItemsByIds, getOrderItemsByOwnerId, getOrderItem] = createBaseEntitySelectors<
    ApplicationState,
    OrderItem,
    typeof NAME
>(moduleSelector, NAME);

export const getOrderItemsByBillId = createEntitySelectorBy('billId')(getOrderItems);

export const getOrderItemIdsByBillId = createSelector(
    getOrderItemsByBillId,
    array.map(({ id }) => id)
);

export const getOrderItemsByBillIds = createEntityPropertyIncludesSelector(getOrderItems, 'billId', 'billIds');
export const getOrderItemsByServiceOrderIds = createEntityPropertyIncludesSelector(
    getOrderItems,
    'serviceOrderId',
    'serviceOrderIds'
);

export const getOrderItemIdsByBillIds = createSelector(
    getOrderItemsByBillIds,
    array.map(({ id }) => id)
);

export const getOrderItemIdsByServiceOrderIds = createSelector(
    getOrderItemsByServiceOrderIds,
    array.map(({ id }) => id)
);

export const getFutureDepositByPastDepositId = createSelector(
    getStringProp('pastDepositId'),
    getOrderItems,
    (pastDepositId, orderItems) =>
        Object.values(orderItems).find(
            (item) =>
                item.data.discriminator === OrderItemType.Deposit && pastDepositId === item.data.value.pastDepositId
        )
);

export const getOrderItemAttachedReservationId = (state: ApplicationState, { orderItemId }: WithOrderItemId) => {
    const orderItem = getOrderItem(state, { id: orderItemId });

    if (!orderItem) return false;

    const serviceOrder = getServiceOrder(state, { id: orderItem.serviceOrderId });

    if (serviceOrder?.data.discriminator === ServiceOrderType.Reservation) {
        return serviceOrder.id;
    }

    if (
        serviceOrder?.data.discriminator === ServiceOrderType.ProductServiceOrder &&
        serviceOrder?.data.value.linkedReservationId
    ) {
        return serviceOrder.data.value.linkedReservationId;
    }

    return false;
};
